import {useDeferredValue, useEffect, useMemo, useRef, useState} from 'react';
import {Sector} from '../SectorTabs';

/* ---------- Types ---------- */
export type FeedItem = {
  id: number | string;
  feed_id: number | string;
  title: string;
  title_ko?: string;
  summary?: string;
  summary_ko?: string;
  source_url?: string;
  sector: string;
  issue_tags?: string[] | string | null;
  gpt_analysis?: string | null;
  policy_summary?: string | null;
  impact_analysis?: string | null;
  investment_note?: string | null;
  provenance_reason?: any;
  published_at?: string;
  sectors?: string[];
  impact_score?: number;
};

export type StockChange = {
  ticker: string;
  corpName: string;

  open?: number;
  high?: number;
  low?: number;
  close?: number;
  volume?: number;
  tradingValue?: number;
  date?: string;

  priceChangePercentage?: number;

  name?: string;
  change_rate?: number;
  changePct?: number;
  change_percentage?: number;

  newsList?: FeedItem[];
};

export const SECTORS: ReadonlyArray<{ key: Sector; label: string }> = [
  {key: 'all', label: '전체'},
  {key: 'bio_healthcare', label: '바이오/헬스케어'},
  {key: 'chip', label: '반도체/칩'},
  {key: 'ev', label: '전기차/배터리'},
  {key: 'energy', label: '에너지/친환경'},
  {key: 'crypto', label: '핀테크/암호화폐'},
  {key: 'defense', label: '방위/항공'},
  {key: 'shipbuilding', label: '조선/해양'},
  {key: 'shipping', label: '해운/물류'},
  {key: 'petrochem', label: '정유/석유화학'},
  {key: 'steel', label: '철강/비철'},
] as const;

/* ---------- Utils ---------- */
const POLICY_KEY = '__POLICY__';

const TAG_CACHE = new Map<any, string[]>();
const PROVENANCE_CACHE = new WeakMap<object, any>();
const PROVENANCE_TEXT_CACHE = new WeakMap<object, string[]>();

function formatTags(raw: any): string[] {
  if (TAG_CACHE.has(raw)) return TAG_CACHE.get(raw)!;

  let out: string[] = [];

  if (!raw) {
    out = [];
  } else if (Array.isArray(raw)) {
    out = raw.map(String).map((s) => s.trim()).filter(Boolean);
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      out = Array.isArray(parsed)
          ? parsed.map(String).map((s) => s.trim()).filter(Boolean)
          : raw.split(',').map((t) => t.trim()).filter(Boolean);
    } catch {
      out = raw.split(',').map((t) => t.trim()).filter(Boolean);
    }
  }

  out = Array.from(new Set(out));
  TAG_CACHE.set(raw, out);
  return out;
}

function matchesSector(feed: FeedItem, sector: Sector) {
  if (sector === 'all') return true;
  if (feed.sector === sector) return true;
  if (Array.isArray(feed.sectors) && feed.sectors.includes(sector)) return true;
  return false;
}

function estimateImpactScore(feed: FeedItem): number {
  const base = 10;

  let reason: any = feed.provenance_reason;

  if (typeof reason === 'string') {
    try {
      reason = JSON.parse(reason);
    } catch {
      reason = null;
    }
  }

  const direct = String(reason?.['직접 영향'] ?? '');
  const indirect = String(reason?.['간접 영향'] ?? '');
  const other = String(reason?.['기타 영향'] ?? '');

  const tags = formatTags(feed.issue_tags);

  const d = Math.min(40, Math.floor(direct.length / 25) * 8);
  const i = Math.min(25, Math.floor(indirect.length / 30) * 6);
  const o = Math.min(15, Math.floor(other.length / 40) * 4);
  const tagBonus = Math.min(20, tags.length * 4);
  const titleBonus = feed.title_ko || feed.title ? 6 : 0;
  const summaryBonus = feed.summary_ko || feed.summary ? 6 : 0;

  const total = base + d + i + o + tagBonus + titleBonus + summaryBonus;
  return Math.max(0, Math.min(100, total));
}

function getInitialQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const url = new URL(window.location.href);
    const v = url.searchParams.get(name);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

function normalizeFeed(fd: any): FeedItem {
  const id = String(fd.feed_id ?? fd.id ?? '');

  return {
    ...(fd as FeedItem),
    id: id || fd.id,
    feed_id: id || fd.feed_id,
    impact_score:
        typeof fd.impact_score === 'number'
            ? fd.impact_score
            : estimateImpactScore(fd as FeedItem),
  };
}

function toNumber(v: any): number | undefined {
  if (v === null || v === undefined || v === '') return undefined;

  if (typeof v === 'string') {
    const clean = v.replace(/,/g, '').trim();

    if (!clean || clean === 'null' || clean === 'undefined') {
      return undefined;
    }

    const n = Number(clean);
    return Number.isFinite(n) ? n : undefined;
  }

  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function pickFirstNumber(obj: any, keys: string[]): number | undefined {
  for (const key of keys) {
    const val = toNumber(obj?.[key]);
    if (val !== undefined) return val;
  }

  return undefined;
}

function pickFirstString(obj: any, keys: string[]): string | undefined {
  for (const key of keys) {
    const val = obj?.[key];

    if (val === null || val === undefined) continue;

    const s = String(val).trim();

    if (!s || s === 'null' || s === 'undefined') continue;

    return s;
  }

  return undefined;
}

function normalizeStockRow(r: any): StockChange | null {
  const ticker = String(r?.ticker ?? r?.code ?? r?.stockCode ?? '').trim();
  const corpName = String(
      r?.corpName ?? r?.corp_name ?? r?.name ?? r?.stockName ?? '',
  ).trim();

  if (!ticker || !corpName) return null;

  const changePct = pickFirstNumber(r, [
    'priceChangePercentage',
    'price_change_percentage',
    'change_rate',
    'changePct',
    'change_percentage',
    'pct_change',
    'chg_pct',
    'diff_pct',
    'change',
  ]);

  const open = pickFirstNumber(r, [
    'open',
    'open_price',
    'start_price',
    'openPrice',
  ]);

  const high = pickFirstNumber(r, [
    'high',
    'high_price',
    'highPrice',
  ]);

  const low = pickFirstNumber(r, [
    'low',
    'low_price',
    'lowPrice',
  ]);

  const close = pickFirstNumber(r, [
    'close',
    'close_price',
    'current_price',
    'price',
    'last_price',
  ]);

  const volume = pickFirstNumber(r, [
    'volume',
    'trading_volume',
    'trade_volume',
  ]);

  const tradingValue = pickFirstNumber(r, [
    'tradingValue',
    'trading_value',
    'amount',
    'trading_amount',
    'trade_amount',
    'market_cap_trading_value',
    'marketTradingValue',
  ]);

  const date = pickFirstString(r, [
    'date',
    'trading_date',
    'latest_date',
    'as_of_date',
    'base_date',
  ]);

  return {
    ticker,
    corpName,
    open,
    high,
    low,
    close,
    volume,
    tradingValue,
    date,
    priceChangePercentage: changePct,
    name: corpName,
    change_rate: changePct,
    changePct,
    change_percentage: changePct,
  };
}

function getProvenanceObject(feed: FeedItem): any {
  if (feed && typeof feed === 'object' && PROVENANCE_CACHE.has(feed as object)) {
    return PROVENANCE_CACHE.get(feed as object);
  }

  let reason: any = feed.provenance_reason;

  if (typeof reason === 'string') {
    try {
      reason = JSON.parse(reason);
    } catch {
      reason = null;
    }
  }

  if (feed && typeof feed === 'object') {
    PROVENANCE_CACHE.set(feed as object, reason);
  }

  return reason;
}

function getProvenanceSections(feed: FeedItem): string[] {
  if (
      feed &&
      typeof feed === 'object' &&
      PROVENANCE_TEXT_CACHE.has(feed as object)
  ) {
    return PROVENANCE_TEXT_CACHE.get(feed as object)!;
  }

  const reason = getProvenanceObject(feed);

  const sections = [
    String(reason?.['직접 영향'] ?? ''),
    String(reason?.['간접 영향'] ?? ''),
    String(reason?.['기타 영향'] ?? ''),
  ].filter(Boolean);

  if (feed && typeof feed === 'object') {
    PROVENANCE_TEXT_CACHE.set(feed as object, sections);
  }

  return sections;
}

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildStockMatchers(stocks: StockChange[]) {
  return stocks
  .filter((stock) => stock?.ticker && stock?.corpName)
  .map((stock) => {
    const ticker = String(stock.ticker).trim();
    const corpName = String(stock.corpName).trim();

    return {
      stock,
      ticker,
      corpName,
      tickerRe: new RegExp(`\\b${escapeRegExp(ticker)}\\b`, 'i'),
      corpNameRe: new RegExp(
          `(^|\\s|\\(|,|/|·)${escapeRegExp(corpName)}(\\s|\\)|,|/|·|$)`,
          'i',
      ),
    };
  });
}

function feedMatchesStockPrecisely(
    sections: string[],
    matcher: ReturnType<typeof buildStockMatchers>[number],
): boolean {
  if (!sections.length) return false;

  for (const section of sections) {
    if (matcher.tickerRe.test(section) || matcher.corpNameRe.test(section)) {
      return true;
    }
  }

  return false;
}

function shouldKeepVisibleStock(
    row: StockChange,
    q: string,
    tickerQuery: string,
) {
  const hasTicker = !!String(row.ticker ?? '').trim();
  const hasCorpName = !!String(row.corpName ?? '').trim();
  const newsCount = row.newsList?.length ?? 0;

  if (!hasTicker || !hasCorpName) return false;
  if (row.ticker === POLICY_KEY) return false;
  if (newsCount <= 0) return false;

  const normalizedTickerQuery = String(tickerQuery ?? '').trim().toLowerCase();

  if (normalizedTickerQuery) {
    return String(row.ticker ?? '').trim().toLowerCase() === normalizedTickerQuery;
  }

  if (!q) return true;

  const qq = q.toLowerCase();

  return (
      String(row.corpName ?? '').toLowerCase().includes(qq) ||
      String(row.ticker ?? '').toLowerCase().includes(qq)
  );
}

/* ---------- Hook ---------- */
export function useDashboardData() {
  const [feeds, setFeeds] = useState<FeedItem[]>([]);
  const [issueTags, setIssueTags] = useState<string[]>([]);
  const [stockChanges, setStockChanges] = useState<StockChange[]>([]);

  const [loading, setLoading] = useState(true);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stockLoaded, setStockLoaded] = useState(false);
  const [stockLoading, setStockLoading] = useState(false);

  const initialSector = (getInitialQueryParam('sector') as Sector) ?? 'all';
  const initialTag = getInitialQueryParam('tag') ?? '';
  const initialQuery = getInitialQueryParam('q') ?? '';
  const initialTickerQuery = getInitialQueryParam('ticker') ?? '';
  const initialExpand = getInitialQueryParam('expand');

  const [sector, setSector] = useState<Sector>(initialSector);
  const [selectedTag, setSelectedTag] = useState(initialTag);
  const [query, setQuery] = useState(initialQuery);
  const [tickerQuery, setTickerQuery] = useState(initialTickerQuery);

  const deferredQuery = useDeferredValue(query);
  const deferredTickerQuery = useDeferredValue(tickerQuery);

  const [visibleCount, setVisibleCount] = useState(30);
  const [expandedTicker, setExpandedTicker] = useState<string | null>(initialExpand);

  const feedAbortRef = useRef<AbortController | null>(null);
  const stockAbortRef = useRef<AbortController | null>(null);
  const didInitialFetchRef = useRef(false);

  const fetchFeeds = async () => {
    feedAbortRef.current?.abort();

    const ac = new AbortController();
    feedAbortRef.current = ac;

    try {
      setLoading(true);
      setError(null);

      const res = await fetch('/api/feed/today', {
        signal: ac.signal,
      });

      if (!res.ok) {
        throw new Error(`feed/today HTTP ${res.status}`);
      }

      const feedJson = await res.json();

      const allFeedsRaw: FeedItem[] = Array.isArray(feedJson) ? feedJson : [];
      const allFeeds: FeedItem[] = allFeedsRaw.map(normalizeFeed);

      setFeeds(allFeeds);

      const tagSet = new Set<string>();

      for (const fd of allFeeds) {
        for (const t of formatTags(fd.issue_tags)) {
          tagSet.add(t);
        }
      }

      setIssueTags(Array.from(tagSet).sort());
    } catch (e: any) {
      if (e?.name === 'AbortError') return;

      console.error('dashboard feed fetch error:', e);

      setFeeds([]);
      setIssueTags([]);
      setError(e?.message ?? '이슈 데이터를 불러오지 못했습니다.');
    } finally {
      setHasFetchedOnce(true);
      setLoading(false);
    }
  };

  const fetchStocksOnce = async (force = false) => {
    if (!force && stockLoaded) return;

    stockAbortRef.current?.abort();

    const ac = new AbortController();
    stockAbortRef.current = ac;

    try {
      setStockLoading(true);

      const res = await fetch('/api/feed/priceChangePercentage', {
        signal: ac.signal,
      });

      if (!res.ok) {
        throw new Error(`priceChangePercentage HTTP ${res.status}`);
      }

      const stockJson = await res.json();

      const mappedStocks: StockChange[] = (Array.isArray(stockJson) ? stockJson : [])
      .map(normalizeStockRow)
      .filter(Boolean) as StockChange[];

      setStockChanges(mappedStocks);
      setStockLoaded(true);
    } catch (e: any) {
      if (e?.name === 'AbortError') return;

      console.error('dashboard stock fetch error:', e);

      setStockChanges([]);
      setStockLoaded(false);
      setError(e?.message ?? '종목 데이터를 불러오지 못했습니다.');
    } finally {
      setStockLoading(false);
    }
  };

  useEffect(() => {
    if (didInitialFetchRef.current) return;

    didInitialFetchRef.current = true;

    void fetchFeeds();

    return () => {
      feedAbortRef.current?.abort();
      stockAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * URL에 ticker 또는 expand가 들어온 경우에는
   * 사용자가 특정 종목 화면으로 직접 진입한 것으로 보고 종목 데이터를 자동 로드한다.
   */
  useEffect(() => {
    if (!initialTickerQuery && !initialExpand) return;

    void fetchStocksOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stockMatchers = useMemo(() => {
    if (!stockLoaded || stockChanges.length === 0) return [];
    return buildStockMatchers(stockChanges);
  }, [stockLoaded, stockChanges]);

  /**
   * 무거운 작업:
   * stockLoaded=false일 때는 종목 매칭을 하지 않는다.
   * 즉, 초기 메인 진입에서는 feeds만 보여주고,
   * 종목 보기 진입 후에만 기사-종목 매핑을 수행한다.
   */
  const groupedAllResult = useMemo(() => {
    if (!stockLoaded || stockChanges.length === 0 || stockMatchers.length === 0) {
      return {
        groupedByTicker: {} as Record<string, StockChange>,
        policyOnlyFeeds: feeds,
      };
    }

    const grouped: Record<string, StockChange> = {};
    const policyFallback: FeedItem[] = [];

    const ensureSlot = (base: StockChange, key: string) => {
      if (!grouped[key]) {
        grouped[key] = {
          ...base,
          name: base.name ?? base.corpName,
          change_rate: base.change_rate ?? base.priceChangePercentage,
          changePct: base.changePct ?? base.priceChangePercentage,
          change_percentage: base.change_percentage ?? base.priceChangePercentage,
          newsList: [],
        };
      }

      return grouped[key];
    };

    for (const feed of feeds) {
      const sections = getProvenanceSections(feed);
      let matched = false;

      if (sections.length > 0) {
        for (const matcher of stockMatchers) {
          if (!feedMatchesStockPrecisely(sections, matcher)) continue;

          const slot = ensureSlot(matcher.stock, matcher.ticker);

          if (!slot.newsList!.some((n) => String(n.feed_id) === String(feed.feed_id))) {
            slot.newsList!.push(feed);
          }

          matched = true;
        }
      }

      if (!matched) {
        policyFallback.push(feed);
      }
    }

    return {
      groupedByTicker: grouped,
      policyOnlyFeeds: policyFallback,
    };
  }, [feeds, stockLoaded, stockChanges.length, stockMatchers]);

  /**
   * 가벼운 작업:
   * 이미 매핑된 결과에서 sector/tag만 필터링한다.
   */
  const filteredStockList = useMemo(() => {
    if (!stockLoaded) return [];

    const base = Object.values(groupedAllResult.groupedByTicker);

    const rows = base
    .map((row) => {
      let news = row.newsList ?? [];

      if (sector !== 'all') {
        news = news.filter((f) => matchesSector(f, sector));
      }

      if (selectedTag) {
        news = news.filter((f) => formatTags(f.issue_tags).includes(selectedTag));
      }

      if (news.length === 0) return null;

      return {
        ...row,
        newsList: news,
      } as StockChange;
    })
    .filter(Boolean) as StockChange[];

    rows.sort((a, b) => {
      const ap = Number(a.priceChangePercentage ?? a.change_rate ?? 0);
      const bp = Number(b.priceChangePercentage ?? b.change_rate ?? 0);

      if (bp !== ap) return bp - ap;

      const atv = Number(a.tradingValue ?? 0);
      const btv = Number(b.tradingValue ?? 0);

      if (btv !== atv) return btv - atv;

      const av = Number(a.volume ?? 0);
      const bv = Number(b.volume ?? 0);

      return bv - av;
    });

    return rows;
  }, [
    stockLoaded,
    groupedAllResult.groupedByTicker,
    sector,
    selectedTag,
  ]);

  const policyOnlyFeeds = useMemo(() => {
    let rows = groupedAllResult.policyOnlyFeeds;

    if (sector !== 'all') {
      rows = rows.filter((f) => matchesSector(f, sector));
    }

    if (selectedTag) {
      rows = rows.filter((f) => formatTags(f.issue_tags).includes(selectedTag));
    }

    return rows;
  }, [
    groupedAllResult.policyOnlyFeeds,
    sector,
    selectedTag,
  ]);

  const displayList = useMemo(() => {
    if (!stockLoaded) return [];

    const q = (deferredQuery || '').toLowerCase().trim();
    const tq = (deferredTickerQuery || '').toLowerCase().trim();

    return filteredStockList.filter((row) => shouldKeepVisibleStock(row, q, tq));
  }, [
    stockLoaded,
    filteredStockList,
    deferredQuery,
    deferredTickerQuery,
  ]);

  const visibleList = useMemo(() => {
    return displayList.slice(0, visibleCount);
  }, [displayList, visibleCount]);

  const resetView = () => {
    setVisibleCount(30);
    setExpandedTicker(null);
  };

  const onChangeSector = (k: Sector) => {
    setSector(k);
    setSelectedTag('');
    setQuery('');
    setTickerQuery('');
    resetView();
  };

  const onChangeTag = (tag: string) => {
    setSelectedTag(tag);
    setTickerQuery('');
    resetView();
  };

  const onChangeQuery = (q: string) => {
    setQuery(q);
    setTickerQuery('');
    resetView();
  };

  const onChangeTicker = async (ticker: string) => {
    await fetchStocksOnce();

    setTickerQuery(String(ticker ?? '').trim());
    setQuery('');
    resetView();
  };

  const onOpenStockView = async () => {
    await fetchStocksOnce();
  };

  const onToggleAccordion = async (ticker: string | null) => {
    if (ticker) {
      await fetchStocksOnce();
    }

    setExpandedTicker(ticker);
  };

  const onLoadMore = () => {
    setVisibleCount((v) => v + 30);
  };

  const onOpenFeed = (_feed: FeedItem) => {
  };

  const refetch = async () => {
    await fetchFeeds();

    if (stockLoaded) {
      await fetchStocksOnce(true);
    }
  };

  return {
    state: {
      loading,
      hasFetchedOnce,
      error,

      stockLoaded,
      stockLoading,

      sector,
      selectedTag,
      query,
      tickerQuery,
      visibleCount,
      expandedTicker,

      issueTags,
      sectors: SECTORS,

      totalCount: displayList.length,
      policyOnlyCount: policyOnlyFeeds.length,
    },

    displayList,
    visibleList,
    stockOptions: stockLoaded ? stockChanges : [],
    policyOnlyFeeds,

    refetch,
    fetchStocksOnce,

    onChangeSector,
    onChangeTag,
    onChangeQuery,
    onChangeTicker,
    onOpenStockView,
    onToggleAccordion,
    onLoadMore,
    onOpenFeed,
  };
}